import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { hasFrontendPermission } from '../permissions';

const makeEmptyDraft = () => ({
  name: '',
  sku: '',
  category: 'General',
  quantity: 0,
  unit_price: 0,
  reorder_level: 5,
  location: '',
  supplier: '',
});

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M6.5 2v9M2 6.5h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

function InventoryStat({ label, value, tone = 'default' }) {
  return (
    <article className={`inventory-stat inventory-stat--${tone}`}>
      <span className="inventory-stat-label">{label}</span>
      <strong className="inventory-stat-value">{value}</strong>
    </article>
  );
}

function InventoryField({ label, children }) {
  return (
    <label className="inventory-field">
      <span className="inventory-field-label">{label}</span>
      {children}
    </label>
  );
}

function InventoryListItem({ item, active, onSelect }) {
  const lowStock = Number(item.quantity || 0) <= Number(item.reorder_level || 0);
  return (
    <button
      type="button"
      className={`inventory-list-item${active ? ' is-active' : ''}`}
      onClick={() => onSelect(item.id)}
    >
      <div className="inventory-list-topline">
        <strong className="inventory-list-title">{item.name || 'Unnamed part'}</strong>
        <span className={`inventory-stock-badge${lowStock ? ' is-low' : ''}`}>
          {lowStock ? 'Low Stock' : 'In Stock'}
        </span>
      </div>
      <div className="inventory-list-meta">
        <span>{item.category || 'General'}</span>
        <span>{item.sku || 'No SKU'}</span>
      </div>
      <div className="inventory-list-meta">
        <span>{item.quantity || 0} on hand</span>
        <span>{money(item.unit_price)}</span>
      </div>
    </button>
  );
}

export function InventoryPage() {
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const auth = loadStoredAuth();
  const token = auth?.token;
  const user = auth?.user;
  const canManageInventory = hasFrontendPermission(user, 'inventory.manage');
  const canDeleteInventory = hasFrontendPermission(user, 'inventory.delete.any');

  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [draft, setDraft] = useState(makeEmptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const loadItems = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/api/inventory', { token });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadItems();
  }, [loadItems, refreshKey]);

  const categories = useMemo(
    () => Array.from(new Set(items.map((item) => item.category).filter(Boolean))).sort(),
    [items],
  );

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return items.filter((item) => {
      if (filterCategory && item.category !== filterCategory) return false;
      if (!term) return true;
      return [item.name, item.sku, item.supplier, item.location]
        .some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [items, searchTerm, filterCategory]);

  useEffect(() => {
    if (!filteredItems.length) {
      setSelectedItemId('');
      return;
    }
    if (!filteredItems.some((item) => item.id === selectedItemId)) {
      setSelectedItemId(filteredItems[0].id);
    }
  }, [filteredItems, selectedItemId]);

  const selectedItem = useMemo(
    () => filteredItems.find((item) => item.id === selectedItemId) || null,
    [filteredItems, selectedItemId],
  );

  const stats = useMemo(() => ({
    total: items.length,
    lowStock: items.filter((item) => Number(item.quantity || 0) <= Number(item.reorder_level || 0)).length,
    value: items.reduce((sum, item) => sum + Number(item.quantity || 0) * Number(item.unit_price || 0), 0),
    categories: categories.length,
  }), [items, categories.length]);

  const updateSelectedItemField = (key, value) => {
    setItems((prev) => prev.map((item) => (
      item.id === selectedItemId ? { ...item, [key]: value } : item
    )));
  };

  const handleRefresh = () => {
    setRefreshKey((value) => value + 1);
  };

  const handleSaveItem = async () => {
    if (!token || !canManageInventory || !selectedItem) return;
    setSaving(true);
    setError('');
    try {
      const updated = await apiFetch(`/api/inventory/${encodeURIComponent(selectedItem.id)}`, {
        token,
        method: 'PUT',
        body: selectedItem,
      });
      setItems((prev) => prev.map((item) => item.id === updated.id ? updated : item));
      pushToast(`Saved ${updated.name}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to save item');
      pushToast(e.message || 'Failed to save item', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateItem = async (event) => {
    event.preventDefault();
    if (!token || !canManageInventory) return;
    setSaving(true);
    setError('');
    try {
      const created = await apiFetch('/api/inventory', {
        token,
        method: 'POST',
        body: draft,
      });
      setItems((prev) => [...prev, created]);
      setSelectedItemId(created.id);
      setDraft(makeEmptyDraft());
      setShowCreateForm(false);
      pushToast(`Created ${created.name}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to create item');
      pushToast(e.message || 'Failed to create item', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!token || !canDeleteInventory || !selectedItem) return;
    if (!window.confirm(`Delete ${selectedItem.name}?`)) return;
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/api/inventory/${encodeURIComponent(selectedItem.id)}`, {
        token,
        method: 'DELETE',
      });
      setItems((prev) => prev.filter((item) => item.id !== selectedItem.id));
      pushToast(`Deleted ${selectedItem.name}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to delete item');
      pushToast(e.message || 'Failed to delete item', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="inventory-page">
        <Skeleton lines={10} />
      </section>
    );
  }

  return (
    <section className="inventory-page">
      <div className="inventory-header">
        <div>
          <span className="inventory-eyebrow">Supply</span>
          <h1 className="inventory-heading">Inventory</h1>
          <p className="inventory-subheading">
            Parts availability, warehouse stock pressure, and supplier-linked items for field execution.
          </p>
        </div>
        <div className="inventory-header-actions">
          <Button variant="secondary" onClick={handleRefresh}>
            <RefreshIcon />
            Refresh
          </Button>
          {canManageInventory ? (
            <Button onClick={() => setShowCreateForm((value) => !value)}>
              <PlusIcon />
              New Part
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <ErrorState message={error} onRetry={loadItems} /> : null}

      <div className="inventory-stats-grid">
        <InventoryStat label="Items" value={stats.total} />
        <InventoryStat label="Low Stock" value={stats.lowStock} tone="danger" />
        <InventoryStat label="Inventory Value" value={money(stats.value)} tone="accent" />
        <InventoryStat label="Categories" value={stats.categories} tone="info" />
      </div>

      {showCreateForm ? (
        <Card className="inventory-create-card" title="Create Inventory Item">
          <form className="inventory-form-grid" onSubmit={handleCreateItem}>
            <InventoryField label="Name">
              <input className="inventory-input" value={draft.name} onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))} placeholder="Condenser fan motor" required />
            </InventoryField>
            <InventoryField label="SKU">
              <input className="inventory-input" value={draft.sku} onChange={(event) => setDraft((prev) => ({ ...prev, sku: event.target.value }))} placeholder="SKU-12345" />
            </InventoryField>
            <InventoryField label="Category">
              <input className="inventory-input" value={draft.category} onChange={(event) => setDraft((prev) => ({ ...prev, category: event.target.value }))} placeholder="HVAC" />
            </InventoryField>
            <InventoryField label="Quantity">
              <input className="inventory-input" type="number" min="0" value={draft.quantity} onChange={(event) => setDraft((prev) => ({ ...prev, quantity: Number(event.target.value || 0) }))} />
            </InventoryField>
            <InventoryField label="Unit Price">
              <input className="inventory-input" type="number" min="0" step="0.01" value={draft.unit_price} onChange={(event) => setDraft((prev) => ({ ...prev, unit_price: Number(event.target.value || 0) }))} />
            </InventoryField>
            <InventoryField label="Reorder Level">
              <input className="inventory-input" type="number" min="0" value={draft.reorder_level} onChange={(event) => setDraft((prev) => ({ ...prev, reorder_level: Number(event.target.value || 0) }))} />
            </InventoryField>
            <InventoryField label="Location">
              <input className="inventory-input" value={draft.location} onChange={(event) => setDraft((prev) => ({ ...prev, location: event.target.value }))} placeholder="Warehouse A" />
            </InventoryField>
            <InventoryField label="Supplier">
              <input className="inventory-input" value={draft.supplier} onChange={(event) => setDraft((prev) => ({ ...prev, supplier: event.target.value }))} placeholder="Carrier" />
            </InventoryField>
            <div className="inventory-form-actions">
              <Button variant="ghost" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>Create Item</Button>
            </div>
          </form>
        </Card>
      ) : null}

      <div className="inventory-layout">
        <Card className="inventory-list-card" title="Parts">
          <div className="inventory-toolbar">
            <input className="inventory-input" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search by name, SKU, supplier, or location" />
            <select className="inventory-input" value={filterCategory} onChange={(event) => setFilterCategory(event.target.value)}>
              <option value="">All categories</option>
              {categories.map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
          </div>

          {filteredItems.length ? (
            <div className="inventory-list">
              {filteredItems.map((item) => (
                <InventoryListItem key={item.id} item={item} active={item.id === selectedItemId} onSelect={setSelectedItemId} />
              ))}
            </div>
          ) : (
            <EmptyState title="No items in this view" message="Adjust filters or create a new inventory item." />
          )}
        </Card>

        <Card className="inventory-detail-card" title={selectedItem ? selectedItem.name : 'Details'}>
          {!selectedItem ? (
            <EmptyState title="No item selected" message="Choose an inventory item to inspect stock, pricing, and supplier details." />
          ) : (
            <div className="inventory-detail">
              <div className="inventory-detail-head">
                <div className="inventory-detail-topline">
                  <span className="inventory-item-id">{selectedItem.id}</span>
                  <span className={`inventory-stock-badge${Number(selectedItem.quantity || 0) <= Number(selectedItem.reorder_level || 0) ? ' is-low' : ''}`}>
                    {Number(selectedItem.quantity || 0) <= Number(selectedItem.reorder_level || 0) ? 'Low Stock' : 'In Stock'}
                  </span>
                </div>
                <p className="inventory-detail-copy">
                  Manage stock thresholds, warehouse placement, and supplier references from one place.
                </p>
              </div>

              <div className="inventory-detail-grid">
                <InventoryField label="Name">
                  {canManageInventory ? (
                    <input className="inventory-input" value={selectedItem.name || ''} onChange={(event) => updateSelectedItemField('name', event.target.value)} />
                  ) : <div className="inventory-readout">{selectedItem.name || 'No name'}</div>}
                </InventoryField>
                <InventoryField label="SKU">
                  {canManageInventory ? (
                    <input className="inventory-input" value={selectedItem.sku || ''} onChange={(event) => updateSelectedItemField('sku', event.target.value)} />
                  ) : <div className="inventory-readout">{selectedItem.sku || 'No SKU'}</div>}
                </InventoryField>
                <InventoryField label="Category">
                  {canManageInventory ? (
                    <input className="inventory-input" value={selectedItem.category || ''} onChange={(event) => updateSelectedItemField('category', event.target.value)} />
                  ) : <div className="inventory-readout">{selectedItem.category || 'General'}</div>}
                </InventoryField>
                <InventoryField label="Quantity">
                  {canManageInventory ? (
                    <input className="inventory-input" type="number" min="0" value={selectedItem.quantity || 0} onChange={(event) => updateSelectedItemField('quantity', Number(event.target.value || 0))} />
                  ) : <div className="inventory-readout">{selectedItem.quantity || 0}</div>}
                </InventoryField>
                <InventoryField label="Unit Price">
                  {canManageInventory ? (
                    <input className="inventory-input" type="number" min="0" step="0.01" value={selectedItem.unit_price || 0} onChange={(event) => updateSelectedItemField('unit_price', Number(event.target.value || 0))} />
                  ) : <div className="inventory-readout">{money(selectedItem.unit_price)}</div>}
                </InventoryField>
                <InventoryField label="Reorder Level">
                  {canManageInventory ? (
                    <input className="inventory-input" type="number" min="0" value={selectedItem.reorder_level || 0} onChange={(event) => updateSelectedItemField('reorder_level', Number(event.target.value || 0))} />
                  ) : <div className="inventory-readout">{selectedItem.reorder_level || 0}</div>}
                </InventoryField>
                <InventoryField label="Location">
                  {canManageInventory ? (
                    <input className="inventory-input" value={selectedItem.location || ''} onChange={(event) => updateSelectedItemField('location', event.target.value)} />
                  ) : <div className="inventory-readout">{selectedItem.location || 'No location'}</div>}
                </InventoryField>
                <InventoryField label="Supplier">
                  {canManageInventory ? (
                    <input className="inventory-input" value={selectedItem.supplier || ''} onChange={(event) => updateSelectedItemField('supplier', event.target.value)} />
                  ) : <div className="inventory-readout">{selectedItem.supplier || 'No supplier'}</div>}
                </InventoryField>
              </div>

              <div className="inventory-detail-actions">
                {canManageInventory ? (
                  <Button onClick={handleSaveItem} disabled={saving}>Save Changes</Button>
                ) : null}
                {canDeleteInventory ? (
                  <Button variant="danger" onClick={handleDeleteItem} disabled={saving}>Delete Item</Button>
                ) : null}
              </div>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}
